import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Play, Pause, RotateCcw, CheckCircle, BookOpen, Volume2, Loader2, Square, Globe, Home, Download, FileText, Eye, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import EmergencyButton from '../components/EmergencyButton';
import { authFetch } from '../lib/authFetch';
import { BackToTopButton } from '../components/NavigationHelpers';
import DocumentHeader from '../components/DocumentHeader';
import ProtectedContent from '../components/ProtectedContent';
import { sanitizeHtml, safeHtml } from '../lib/sanitize';
import { weekLabel, bareWeekLabel, isTwoWeekWindow } from '../lib/weekLabels';
import MachineTranslationBadge from '../components/MachineTranslationBadge';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function WeeklyReadingPage() {
  useTheme4A();
  const navigate = useNavigate();
  const { readingId } = useParams();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);

  // TTS state
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedLang, setSelectedLang] = useState(user?.language || 'en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [pdfLang, setPdfLang] = useState(user?.language || 'en');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const audioRef = useRef(null);

  const LANGUAGES = {
    en: 'English', ru: 'Russian', uk: 'Ukrainian', pt: 'Portuguese',
    fr: 'French', it: 'Italian', es: 'Spanish', ko: 'Korean', zh: 'Mandarin', 'zh-TW': 'Mandarin (Traditional)'
  };

  // Fetch reading from database
  useEffect(() => {
    const fetchReading = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/readings/${readingId}?lang=${encodeURIComponent(selectedLang || 'en')}`;
        const response = await authFetch(url);
        if (response.ok) {
          const data = await response.json();
          setReading({
            code: data.code,
            reading_id: data.reading_id,
            title: data.title,
            subtitle: data.subtitle,
            description: data.description,
            estimatedTime: data.estimated_time || 90,
            week: data.week,
            content: data.content,
            language: data.language || 'en',
            machine_translated: !!data.machine_translated,
            translation_pending: !!data.translation_pending,
            requested_language: data.requested_language,
            languages_available: data.languages_available || ['en'],
            pdfUrl: data.pdf_url,
            pdf_urls: data.pdf_urls || {},
            diagrams: data.diagrams || [],
            supplements: data.supplements || [],
            image_urls: data.image_urls || [],
            documents: data.documents || [],
            self_work: data.self_work || null,
          });
        } else {
          setReading(null);
        }
      } catch (error) {
        console.error('Failed to fetch reading:', error);
        setReading(null);
      } finally {
        setLoading(false);
      }
    };
    fetchReading();
  }, [readingId, selectedLang]);

  const requiredTime = (reading?.estimatedTime || 30) * 60;
  const progress = Math.min(100, (timeSpent / requiredTime) * 100);

  // Load saved reading time on mount
  useEffect(() => {
    if (!readingId) return;
    const loadTime = async () => {
      try {
        const resp = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/readings/${readingId}/time`);
        if (resp.ok) {
          const data = await resp.json();
          if (data.total_seconds > 0) setTimeSpent(data.total_seconds);
        }
      } catch (e) {
        // Network blip on initial time load — start the timer at 0.
        console.debug('[Reading] failed to fetch prior time:', e?.message);
      }
    };
    loadTime();
  }, [readingId]);

  // Save reading time to backend
  const saveTimeToServer = useCallback(async (seconds) => {
    if (seconds <= 0) return;
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/readings/${readingId}/log-time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seconds })
      });
    } catch (e) {
      // Time-logging is best-effort.  The next tick will retry.
      console.debug('[Reading] failed to log time:', e?.message);
    }
  }, [readingId]);

  // Timer effect — ticks every second, saves every 30s
  const lastSavedRef = useRef(0);
  useEffect(() => {
    let interval;
    if (!isPaused && !isCompleted) {
      interval = setInterval(() => {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          // Save to server every 30 seconds
          if (newTime - lastSavedRef.current >= 30) {
            saveTimeToServer(newTime - lastSavedRef.current);
            lastSavedRef.current = newTime;
          }
          if (newTime >= requiredTime) {
            setIsCompleted(true);
            setIsPaused(true);
            saveTimeToServer(newTime - lastSavedRef.current);
            lastSavedRef.current = newTime;
            toast.success('Reading completed!');
          }
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPaused, isCompleted, requiredTime, readingId, saveTimeToServer]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePause = () => {
    setIsPaused(true);
    setShowPauseDialog(true);
    // Save accumulated time on pause
    const unsaved = timeSpent - lastSavedRef.current;
    if (unsaved > 0) {
      saveTimeToServer(unsaved);
      lastSavedRef.current = timeSpent;
    }
  };

  const handleResume = () => {
    setIsPaused(false);
    setShowPauseDialog(false);
  };

  const handleRestart = () => {
    setTimeSpent(0);
    setIsCompleted(false);
    setIsPaused(true);
    setShowPauseDialog(false);
  };

  const handleMarkComplete = async () => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/reading-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          reading_id: readingId,
          time_spent: timeSpent,
          completed: true
        })
      });
      toast.success('Progress saved!');
      navigate('/library');
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  // Strip HTML tags for TTS (sanitised first to neutralise any XSS payload
  // before the browser touches it via innerHTML).
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitizeHtml(html);
    return tmp.textContent || tmp.innerText || '';
  };

  const handleListen = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    const plainText = stripHtml(reading.content);
    if (!plainText.trim()) {
      toast.error('No reading content available to read aloud');
      return;
    }

    // Limit TTS to first ~3000 characters to avoid timeout on long documents
    const ttsText = plainText.length > 3000 
      ? plainText.substring(0, plainText.lastIndexOf('.', 3000) + 1) || plainText.substring(0, 3000)
      : plainText;

    setIsGeneratingAudio(true);
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/tts/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          text: ttsText,
          language: selectedLang,
          voice: 'onyx'
        })
      });

      if (response.ok) {
        const data = await response.json();
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audioRef.current = audio;
        
        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setIsPlaying(false);
          audioRef.current = null;
          toast.error('Audio playback failed');
        };

        await audio.play();
        setIsPlaying(true);
        // Auto-start the reading timer when listening
        if (isPaused && !isCompleted) {
          setIsPaused(false);
        }
      } else {
        toast.error('Failed to generate audio. Please try again.');
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Failed to generate audio');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-blue-200 z-40 h-16 px-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/library?tab=programme')} data-testid="back-button">
            <ArrowLeft className="w-5 h-5 text-blue-600" />
          </button>
          <button onClick={() => navigate('/dashboard')} data-testid="home-button" className="p-1 rounded-md hover:bg-blue-50">
            <Home className="w-4 h-4 text-blue-500" />
          </button>
        </div>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <EmergencyButton />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-300" />
            </div>
          ) : !reading ? (
            <div className="text-center py-20">
              <BookOpen className="w-12 h-12 text-blue-200 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Reading Not Found</h2>
              <p className="text-blue-200">This reading hasn't been created yet.</p>
              <button onClick={() => navigate('/library')} className="mt-4 px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium">
                Back to Library
              </button>
            </div>
          ) : (
          <>
          {/* Reading Header  --  short status pill (kept on the dark hero
              background); the editorial Document Header sits inside the white
              .arp-document panel below. */}
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-blue-200 text-blue-800 text-xs font-mono font-bold px-3 py-1 rounded">
              {reading.code}
            </span>
            <span className="text-blue-200 text-sm">{reading.week === 0 ? 'Introduction' : bareWeekLabel(reading.week)}</span>
          </div>

          {/* Timer Card */}
          <div className="bg-white rounded-xl p-6 mb-6" data-testid="reading-timer">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Reading Time</span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-blue-700">{formatTime(timeSpent)}</p>
                <p className="text-xs text-gray-500">of {reading.estimatedTime} min required</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${isCompleted ? 'bg-green-500' : 'bg-blue-500'}`}
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Timer Controls */}
            <div className="flex gap-3">
              {!isPaused ? (
                <Button
                  onClick={handlePause}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="pause-reading-btn"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pause Reading
                </Button>
              ) : (
                <Button
                  onClick={handleResume}
                  disabled={isCompleted}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="start-reading-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {timeSpent > 0 ? 'Continue Reading' : 'Start Reading'}
                </Button>
              )}
              
              <Button
                onClick={handleRestart}
                variant="outline"
                className="border-gray-300"
                data-testid="restart-reading-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {isCompleted && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-bold text-green-800">Reading Time Complete!</p>
                  <p className="text-sm text-green-700">You can now mark this reading as finished.</p>
                </div>
              </div>
            )}
          </div>

          {/* Listen / TTS Controls */}
          <div className="bg-white rounded-xl p-4 mb-4" data-testid="tts-controls">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">Listen to Reading</span>
              </div>
              {/* Language picker */}
              <div className="relative">
                <button
                  onClick={() => setShowLangPicker(!showLangPicker)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700"
                  data-testid="language-picker-btn"
                >
                  <Globe className="w-3 h-3" />
                  {LANGUAGES[selectedLang]}
                </button>
                {showLangPicker && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                    {Object.entries(LANGUAGES).map(([code, name]) => (
                      <button
                        key={code}
                        onClick={() => { setSelectedLang(code); setShowLangPicker(false); }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${
                          selectedLang === code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'
                        }`}
                        data-testid={`lang-${code}`}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <Button
              onClick={handleListen}
              disabled={isGeneratingAudio}
              className={`w-full h-12 rounded-lg font-medium text-sm ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
              data-testid="listen-btn"
            >
              {isGeneratingAudio ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Audio...</>
              ) : isPlaying ? (
                <><Square className="w-4 h-4 mr-2" /> Stop Listening</>
              ) : (
                <><Volume2 className="w-4 h-4 mr-2" /> Listen in {LANGUAGES[selectedLang]}</>
              )}
            </Button>
            {selectedLang !== 'en' && (
              <p className="text-xs text-blue-600 mt-2 text-center">
                The reading will be translated to {LANGUAGES[selectedLang]} and read aloud
              </p>
            )}
          </div>

          {/* PDF Documents Section */}
          {reading.pdf_urls && Object.keys(reading.pdf_urls).length > 0 && (
            <div className="bg-white rounded-xl p-4 mb-4" data-testid="pdf-documents-section">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-800">PDF Documents</span>
              </div>

              {/* Language Tabs */}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {Object.entries(reading.pdf_urls).map(([langCode, url]) => (
                  <button
                    key={langCode}
                    onClick={() => setPdfLang(langCode)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      pdfLang === langCode
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                    }`}
                    data-testid={`pdf-lang-${langCode}`}
                  >
                    {LANGUAGES[langCode] || langCode.toUpperCase()}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mb-3">
                <Button
                  onClick={() => setShowPdfViewer(!showPdfViewer)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm h-10"
                  data-testid="pdf-view-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPdfViewer ? 'Hide Document' : 'Read in App'}
                </Button>
                <a
                  href={reading.pdf_urls[pdfLang] || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-300 bg-white hover:bg-zinc-50 rounded-md h-10 px-4 text-sm font-medium transition-colors"
                  data-testid="pdf-download-btn"
                >
                  <Download className="w-4 h-4" />
                  Download / Print
                </a>
              </div>

              {/* In-App PDF Viewer */}
              {showPdfViewer && reading.pdf_urls[pdfLang] && (
                <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid="pdf-viewer">
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(reading.pdf_urls[pdfLang])}&embedded=true`}
                    title={`${reading.title} - ${LANGUAGES[pdfLang] || pdfLang}`}
                    className="w-full bg-zinc-50"
                    style={{ height: '70vh', minHeight: '500px' }}
                  />
                </div>
              )}

              <p className="text-xs text-zinc-400 mt-2 text-center">
                {LANGUAGES[pdfLang] || pdfLang} version: {Object.keys(reading.pdf_urls).length} languages available
              </p>
            </div>
          )}

          {/* Documents for this Week  --  one place that lists every file
              attached to this Programme week:  the main reading PDF in
              every language, every diagram and every worksheet.  Always
              opens in a new tab via a real <a target="_blank">. */}
          {Array.isArray(reading.documents) && reading.documents.length > 1 && (
            <div className="bg-white rounded-xl p-4 mb-4 border border-zinc-200" data-testid="documents-for-week-section">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-zinc-700" />
                <span className="text-sm font-semibold text-zinc-900">
                  Documents for {reading[`title_${selectedLang}`] || reading.title}
                </span>
                <span className="ml-auto text-[11px] text-zinc-500">
                  {reading.documents.length} file{reading.documents.length === 1 ? '' : 's'}
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed mb-3">
                Every file we have for this week.  Tap to open in a new tab.
              </p>
              <ul className="space-y-1.5" data-testid="documents-for-week-list">
                {reading.documents.map((doc, idx) => {
                  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${doc.url}`;
                  const dotColour = {
                    reading: 'bg-blue-500',
                    diagram: 'bg-amber-500',
                    worksheet: 'bg-emerald-500',
                  }[doc.kind] || 'bg-zinc-400';
                  return (
                    <li key={`${doc.kind}-${doc.url}-${idx}`}>
                      <a
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition-colors"
                        data-testid={`document-link-${doc.kind}-${doc.slug || doc.language || idx}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${dotColour} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-zinc-900 truncate block">{doc.label}</span>
                          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                            {doc.extension || doc.kind}
                          </span>
                        </div>
                        <Download className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Reading Content  --  rendered as an editorial document with a
              big serif title, italic subtitle and grey eyebrow that mirrors
              the printable PDF.  The reading body inherits .arp-document's
              serif typography. */}
          <div className="arp-document mb-6" data-testid="reading-content">
            {(() => {
              const localisedTitle = reading[`title_${selectedLang}`] || reading.title;
              return (
                <DocumentHeader
                  title="Axiom Recovery Programme (ARP)"
                  subtitle={reading.subtitle || localisedTitle}
                  eyebrow={localisedTitle}
                  testIdPrefix="reading"
                />
              );
            })()}
            {selectedLang !== 'en' && reading.translation_pending && (
              <div
                className="my-3 inline-flex items-start gap-2 px-3 py-2 rounded-lg border bg-blue-50 border-blue-200 text-blue-900 text-[11px] leading-snug"
                data-testid="reading-translation-pending"
              >
                <Loader2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 animate-spin" />
                <div>
                  <p className="font-bold">Translation in progress  ·  {LANGUAGES[selectedLang] || selectedLang}</p>
                  <p className="mt-0.5">The {LANGUAGES[selectedLang] || selectedLang.toUpperCase()} translation of this reading is being generated.  In the meantime you are reading the English original.  Refresh in a few minutes to see the translation.</p>
                </div>
              </div>
            )}
            {reading.language !== 'en' && (
              <MachineTranslationBadge
                language={reading.language}
                machineTranslated={!!reading.machine_translated}
                className="my-3"
                testId="reading-translation-badge"
              />
            )}
            <ProtectedContent>
              <div
                className="reading-content"
                dangerouslySetInnerHTML={safeHtml(reading.content)}
              />
            </ProtectedContent>

            {/* Diagrams section  --  surfaces every ARP_Diagram_* upload
                associated with this week as a separate gallery, complete
                with captions, BELOW the inline body.  The inline figures
                appended to the body remain visible too so the reading
                still reads as a single document. */}
            {Array.isArray(reading.diagrams) && reading.diagrams.length > 0 && (
              <section className="arp-diagrams-section" data-testid="reading-diagrams-section">
                <h2 className="arp-diagrams-heading">Diagrams</h2>
                <p className="arp-diagrams-subtitle">Reference illustrations for this week.</p>
                <div className="arp-diagrams-grid">
                  {reading.diagrams.map((d) => (
                    <figure key={d.slug} className="arp-figure" data-testid={`reading-diagram-${d.slug}`}>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                        alt={d.caption || d.slug}
                        loading="lazy"
                      />
                      {d.caption && <figcaption>{d.caption}</figcaption>}
                    </figure>
                  ))}
                </div>
              </section>
            )}

            {/* Supplement documents: companion full-text materials attached
                to this week's reading (e.g. Twelve Traditions).  Rendered
                as collapsible sections beneath the main body so the reader
                can drop into them without leaving the page.  Each also has
                an inline download link to the original file. */}
            {Array.isArray(reading.supplements) && reading.supplements.length > 0 && (
              <section className="arp-supplements-section mt-8" data-testid="reading-supplements-section">
                <h2 className="arp-diagrams-heading">Supplements</h2>
                <p className="arp-diagrams-subtitle">Additional reference material for this week.</p>
                <div className="space-y-4 mt-3">
                  {reading.supplements.map((s) => (
                    <details
                      key={s.slug}
                      className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
                      data-testid={`reading-supplement-${s.slug}`}
                    >
                      <summary className="cursor-pointer px-4 py-3 font-bold text-sm text-zinc-900 hover:bg-zinc-50 flex items-center justify-between gap-3">
                        <span>{s.title || s.slug}</span>
                        {s.download_url && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${s.download_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-[11px] uppercase tracking-wider text-blue-700 hover:underline"
                            data-testid={`reading-supplement-${s.slug}-download`}
                          >
                            Download
                          </a>
                        )}
                      </summary>
                      <div
                        className="arp-document px-4 py-4 border-t border-zinc-100"
                        dangerouslySetInnerHTML={safeHtml(s.body || '')}
                      />
                    </details>
                  ))}
                </div>
              </section>
            )}
            {/* Batch 7 item 5: copyright footer on every daily reading screen. */}
            <p
              className="text-xs mt-6 text-center"
              style={{ color: '#444444' }}
              data-testid="reading-copyright-line"
            >
              © Graeme Tudor. Protected by copyright. Not to be copied or shared.
            </p>
          </div>

          {/* Self-Work CTA  --  every Programme reading from Week 3 onward
              has a paired Self-Work workbook.  When questions exist we show
              a prominent "Open the Week N Self-Work" button;  when they
              don't, we show a clear note explaining why. */}
          {reading.self_work && (
            <div className="mb-4" data-testid="reading-sw-callout">
              {reading.self_work.available ? (
                <button
                  onClick={() => navigate(reading.self_work.route)}
                  className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 text-left hover:bg-blue-100 transition-colors flex items-center gap-3"
                  data-testid="reading-sw-open-btn"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-zinc-900">Self-Work for {bareWeekLabel(reading.self_work.week)} of the programme</p>
                    <p className="text-[11px] text-zinc-600 mt-0.5">
                      {reading.self_work.question_count} question{reading.self_work.question_count === 1 ? '' : 's'}  ·  Type or speak your answers  ·  Auto-saves every 5 seconds.
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                </button>
              ) : (
                <div className="w-full bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-center" data-testid="reading-sw-unavailable">
                  <p className="text-[12px] text-zinc-600 leading-relaxed">
                    {reading.self_work.reason || 'No Self-Work for this week.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Complete Button */}
          <Button
            onClick={handleMarkComplete}
            disabled={!isCompleted}
            className={`w-full h-14 rounded-xl font-bold text-sm uppercase tracking-wide shadow-lg ${
              isCompleted 
                ? 'bg-green-600 hover:bg-green-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="complete-reading-btn"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            {isCompleted ? 'Mark as Complete' : 'Complete Reading Time First'}
          </Button>
          </>
          )}
        </div>
      </main>

      {/* Pause Dialog */}
      {showPauseDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Reading Paused</h3>
            <p className="text-sm text-gray-600 mb-4">
              Do you need to take a break? You can resume where you left off or go back to the beginning.
            </p>
            <div className="space-y-3">
              <Button
                onClick={handleResume}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Play className="w-4 h-4 mr-2" />
                Continue Reading
              </Button>
              <Button
                onClick={handleRestart}
                variant="outline"
                className="w-full"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Start from Beginning
              </Button>
              <Button
                onClick={() => navigate('/library')}
                variant="ghost"
                className="w-full text-gray-500"
              >
                Save & Exit
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <BackToTopButton />
      <Footer />
    </div>
  );
}

export default WeeklyReadingPage;
